import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Download, 
  Share2, 
  TrendingUp,
  Eye,
  FileText,
  Filter,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SHARE_METHOD_COLORS = {
  pdf: 'bg-red-100 text-red-700',
  linkedin: 'bg-blue-100 text-blue-700',
  twitter: 'bg-gray-100 text-gray-900',
  whatsapp: 'bg-green-100 text-green-700',
  telegram: 'bg-sky-100 text-sky-700',
  teams: 'bg-indigo-100 text-indigo-700',
  slack: 'bg-purple-100 text-purple-700',
  copy_link: 'bg-purple-100 text-purple-700'
};

const SHARE_METHOD_ICONS = {
  pdf: '📄',
  linkedin: '💼',
  twitter: '🐦',
  whatsapp: '💬',
  telegram: '✈️',
  teams: '👥',
  slack: '💬',
  copy_link: '🔗'
};

const ExportsPage = () => {
  const [exports, setExports] = useState([]);
  const [views, setViews] = useState([]);
  const [stats, setStats] = useState(null);
  const [viewStats, setViewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('exports'); // 'exports' or 'views'
  
  // Filters
  const [filters, setFilters] = useState({
    days: 30,
    share_method: '',
    action: '',
    project_id: '',
    user_email: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters, activeTab]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      if (activeTab === 'exports') {
        const response = await axios.get(`${API}/admin/exports`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            days: filters.days,
            share_method: filters.share_method || undefined,
            project_id: filters.project_id || undefined,
            user_email: filters.user_email || undefined
          }
        });
        setExports(response.data.exports);
        setStats(response.data.stats);
      } else {
        const response = await axios.get(`${API}/admin/project-views`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            days: filters.days,
            action: filters.action || undefined,
            project_id: filters.project_id || undefined
          }
        });
        setViews(response.data.views);
        setViewStats(response.data.stats);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const data = activeTab === 'exports' ? exports : views;
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    let csvContent = '';
    
    if (activeTab === 'exports') {
      csvContent = 'Date,Type,Method,Project,User,Company\n';
      data.forEach(item => {
        const exp = item.export;
        csvContent += `${exp.timestamp},${exp.export_type},${exp.share_method},${item.project_title},${item.user_name},${item.company_name}\n`;
      });
    } else {
      csvContent = 'Date,Action,Project,IP,Referrer\n';
      data.forEach(item => {
        const view = item.view;
        csvContent += `${view.timestamp},${view.action},${item.project_title},${view.viewer_ip || 'N/A'},${view.referrer || 'Direct'}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('CSV exported successfully!');
  };

  // Chart data for views
  const chartData = viewStats?.chart_data ? {
    labels: viewStats.chart_data.map(d => d.date),
    datasets: [
      {
        label: 'Views',
        data: viewStats.chart_data.map(d => d.count),
        borderColor: '#62D84E',
        backgroundColor: 'rgba(98, 216, 78, 0.1)',
        tension: 0.3
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: `Views Over Last ${filters.days} Days`
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  if (loading && !stats && !viewStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
              Exports & Analytics Tracking
            </h1>
            <p className="text-gray-600 mt-1">
              Track project shares, exports, and public page visibility
            </p>
          </div>
          <Button onClick={exportToCSV} className="btn-snow">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('exports')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'exports'
              ? 'border-b-2 text-green-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          style={{ borderColor: activeTab === 'exports' ? '#62D84E' : 'transparent' }}
        >
          <Share2 className="w-4 h-4 inline mr-2" />
          Shares & Exports
        </button>
        <button
          onClick={() => setActiveTab('views')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'views'
              ? 'border-b-2 text-green-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          style={{ borderColor: activeTab === 'views' ? '#62D84E' : 'transparent' }}
        >
          <Eye className="w-4 h-4 inline mr-2" />
          Public Views
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        {activeTab === 'exports' && stats && (
          <>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Exports</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: '#1B3240' }}>
                      {stats.total_exports}
                    </p>
                  </div>
                  <FileText className="w-10 h-10 opacity-20" style={{ color: '#62D84E' }} />
                </div>
              </CardContent>
            </Card>

            {Object.entries(stats.by_method).map(([method, count]) => (
              <Card key={method}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 capitalize">{method.replace('_', ' ')}</p>
                      <p className="text-3xl font-bold mt-1" style={{ color: '#1B3240' }}>
                        {count}
                      </p>
                    </div>
                    <span className="text-4xl">{SHARE_METHOD_ICONS[method] || '📊'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {activeTab === 'views' && viewStats && (
          <>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Views</p>
                    <p className="text-3xl font-bold mt-1" style={{ color: '#1B3240' }}>
                      {viewStats.total_views}
                    </p>
                  </div>
                  <Eye className="w-10 h-10 opacity-20" style={{ color: '#62D84E' }} />
                </div>
              </CardContent>
            </Card>

            {Object.entries(viewStats.by_action).map(([action, count]) => (
              <Card key={action}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 capitalize">{action.replace('_', ' ')}</p>
                      <p className="text-3xl font-bold mt-1" style={{ color: '#1B3240' }}>
                        {count}
                      </p>
                    </div>
                    <TrendingUp className="w-10 h-10 opacity-20" style={{ color: '#62D84E' }} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>

      {/* Chart - Only for views */}
      {activeTab === 'views' && (
        <Card>
          <CardHeader>
            <CardTitle>Views Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData && chartData.datasets[0].data.length > 0 ? (
              <div style={{ height: '300px' }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <BarChart3 className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">No view data available</p>
                <p className="text-sm mt-2">Public project views will appear here once users start viewing shared projects</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Projects - Only for exports */}
      {activeTab === 'exports' && (
        <Card>
          <CardHeader>
            <CardTitle>Top Shared Projects</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.top_projects && stats.top_projects.length > 0 ? (
              <div className="space-y-3">
                {stats.top_projects.map((project, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium" style={{ color: '#1B3240' }}>{project.title}</p>
                      <p className="text-sm text-gray-600">{project.project_id}</p>
                    </div>
                    <Badge style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}>
                      {project.shares} shares
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Share2 className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">No shared projects yet</p>
                <p className="text-sm mt-2">Project share statistics will appear here once users start sharing projects</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Period</label>
              <select
                value={filters.days}
                onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
            </div>

            {activeTab === 'exports' && (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Share Method</label>
                <select
                  value={filters.share_method}
                  onChange={(e) => setFilters({ ...filters, share_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All methods</option>
                  <option value="pdf">PDF</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="telegram">Telegram</option>
                  <option value="teams">Teams</option>
                  <option value="slack">Slack</option>
                  <option value="copy_link">Copy Link</option>
                </select>
              </div>
            )}

            {activeTab === 'views' && (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Action Type</label>
                <select
                  value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">All actions</option>
                  <option value="view">Views</option>
                  <option value="apply_click">Apply Clicks</option>
                  <option value="contact_click">Contact Clicks</option>
                </select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {activeTab === 'exports' ? 'Recent Exports & Shares' : 'Recent Public Views'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {activeTab === 'exports' ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Method</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Project</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">User</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exports.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">
                          {new Date(item.export.timestamp).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <Badge className={SHARE_METHOD_COLORS[item.export.share_method] || 'bg-gray-100'}>
                            {SHARE_METHOD_ICONS[item.export.share_method] || ''} {item.export.share_method}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-sm">{item.project_title}</p>
                            <p className="text-xs text-gray-500">{item.project_id}</p>
                          </div>
                        </td>
                        <td className="p-3 text-sm">{item.user_name}</td>
                        <td className="p-3 text-sm">{item.company_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Action</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Project</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">IP</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {views.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">
                          {new Date(item.view.timestamp).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <Badge className="bg-blue-100 text-blue-700">
                            {item.view.action}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-sm">{item.project_title}</p>
                            <p className="text-xs text-gray-500">{item.project_id}</p>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">{item.view.viewer_ip || 'N/A'}</td>
                        <td className="p-3 text-sm text-gray-600">
                          {item.view.referrer ? new URL(item.view.referrer).hostname : 'Direct'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {((activeTab === 'exports' && exports.length === 0) || 
                (activeTab === 'views' && views.length === 0)) && (
                <div className="text-center py-8 text-gray-500">
                  No data available for the selected filters
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ExportsPage;
